import {DefaultPrivacyLevel, datadogRum} from '@datadog/browser-rum';

import {
  APPLICATION_ID_PLACEHOLDER,
  CLIENT_TOKEN_PLACEHOLDER,
  DATADOG_ELEMENT_ID,
  LOGIN_APP_VERSION_PLACEHOLDER,
  SAMPLE_RATE_PLACEHOLDER,
  SERVICE_NAME_PLACEHOLDER,
  SESSION_REPLAY_SAMPLE_RATE_PLACEHOLDER,
} from './datadogPlaceholders';

type ValidatedDatadogConfig = {
  applicationId: string;
  clientToken: string;
  sampleRate: number;
  sessionReplaySampleRate: number;
  service: string;
  version: string;
};

const validateDatadogConfig = (values: unknown): ValidatedDatadogConfig | null => {
  if (typeof values !== 'object' || values === null) {
    return null;
  }

  const config = values as Record<string, unknown>;

  // Verify all required fields exist and are not placeholders
  if (
    config.applicationId === undefined ||
    config.applicationId === APPLICATION_ID_PLACEHOLDER ||
    config.clientToken === undefined ||
    config.clientToken === CLIENT_TOKEN_PLACEHOLDER ||
    config.service === undefined ||
    config.service === SERVICE_NAME_PLACEHOLDER ||
    config.sampleRate === undefined ||
    config.sampleRate === SAMPLE_RATE_PLACEHOLDER ||
    config.sessionReplaySampleRate === undefined ||
    config.sessionReplaySampleRate === SESSION_REPLAY_SAMPLE_RATE_PLACEHOLDER ||
    config.version === undefined ||
    config.version === LOGIN_APP_VERSION_PLACEHOLDER
  ) {
    return null;
  }

  const sampleRateNumber = parseFloat(String(config.sampleRate));
  const sessionReplaySampleRateNumber = parseFloat(String(config.sessionReplaySampleRate));

  return {
    applicationId: String(config.applicationId),
    clientToken: String(config.clientToken),
    service: String(config.service),
    sampleRate: isNaN(sampleRateNumber) ? 0 : sampleRateNumber,
    sessionReplaySampleRate: isNaN(sessionReplaySampleRateNumber)
      ? 0
      : sessionReplaySampleRateNumber,
    version: String(config.version),
  };
};

export const initializeDatadog = (): void => {
  const datadogElement = document.getElementById(DATADOG_ELEMENT_ID);
  if (!datadogElement?.textContent) {
    return;
  }

  let values: unknown;
  try {
    values = JSON.parse(datadogElement.textContent);
  } catch (e) {
    console.error('Failed to parse Datadog config:', e);
    return;
  }

  const validated = validateDatadogConfig(values);
  if (!validated) {
    return;
  }

  const {applicationId, clientToken, sampleRate, sessionReplaySampleRate, service, version} =
    validated;

  datadogRum.init({
    applicationId,
    clientToken,
    site: 'datadoghq.com',
    service,
    version,
    sessionSampleRate: sampleRate,
    sessionReplaySampleRate,
    allowedTracingUrls: [/https:\/\/(.*\.)?dagster\.cloud/, /https:\/\/(.*\.)?dagster\.plus/],
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: DefaultPrivacyLevel.ALLOW,
  });

  datadogRum.startSessionReplayRecording();
};
